import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { createOrder } from '../api';
import PickupSelector from './PickupSelector';
import { DELIVERY_ZONES, calcTotals, fmtRub } from './deliveryRules';

/* ========== Клиентская диагностика: логи на бэкенд + цели Метрики ========== */
function clientLog(level, payload) {
    try {
        fetch('/api/client-log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                level,
                ts: new Date().toISOString(),
                url: window.location.href,
                ua: window.navigator.userAgent,
                payload,
            }),
        }).catch(() => { });
    } catch (_) { }
}

function goal(name, params) {
    try {
        if (window.ym) {
            if (params) window.ym(112121340, 'reachGoal', name, params);
            else window.ym(112121340, 'reachGoal', name);
        }
    } catch (_) { }
}

function errText(e) {
    if (!e) return 'Неизвестная ошибка';
    if (typeof e === 'string') return e;
    return e.message || e.detail || String(e);
}

function withTimeout(promise, ms = 25000) {
    return Promise.race([
        promise,
        new Promise((_, reject) =>
            setTimeout(
                () => reject(new Error('Сервер не ответил вовремя. Проверьте соединение и попробуйте ещё раз.')),
                ms
            )
        ),
    ]);
}

const CartModal = ({
    isOpen, onClose, cart, removeFromCart, updateQuantity, totalPrice,
    userId, metrikaId, products, addToCart, cartPromos = [],
    isAuthenticated = false, bonusBalance = 0, onBonusUsed,
}) => {
    const [step, setStep] = useState('cart');
    const [processing, setProcessing] = useState(false);
    const [applyBonus, setApplyBonus] = useState(false);
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [error, setError] = useState('');
    const [errorKind, setErrorKind] = useState('validation');
    const [form, setForm] = useState({ full_name: '', phone: '', email: '' });
    const [pickup, setPickup] = useState(null);
    const [zoneId, setZoneId] = useState('msk');
    const [consent, setConsent] = useState(false);
    const [marketing, setMarketing] = useState(false);

    // Кросс-сейл
    const recommendations = useMemo(() => {
        if (!products || !products.length || !cart.length) return [];
        const inCart = new Set(cart.map((i) => i.id));
        const cartBrands = new Set(cart.map((i) => i.brand).filter(Boolean));
        const cartCats = new Set(cart.map((i) => (i.category || '').toLowerCase()));
        const COMPLEMENT = {
            'очищающие средства': ['spf', 'увлажняющие средства', 'сыворотки'],
            'сыворотки': ['увлажняющие средства', 'spf', 'очищающие средства'],
            'увлажняющие средства': ['spf', 'очищающие средства', 'сыворотки'],
            'spf': ['очищающие средства', 'увлажняющие средства', 'сыворотки'],
            'наборы': ['spf', 'очищающие средства', 'увлажняющие средства'],
        };
        const wanted = new Set();
        cartCats.forEach((c) => (COMPLEMENT[c] || []).forEach((x) => wanted.add(x)));
        return products
            .filter((p) => !inCart.has(p.id))
            .map((p) => {
                let score = 0;
                const cat = (p.category || '').toLowerCase();
                if (cartBrands.has(p.brand)) score += 3;
                if (wanted.has(cat)) score += 3;
                if (cat && !cartCats.has(cat)) score += 1;
                return { p, score };
            })
            .filter((x) => x.score > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 3)
            .map((x) => x.p);
    }, [cart, products]);

    const totals = calcTotals(cart, zoneId, cartPromos);

    // ─── НОВОЕ: расчёт применяемых бонусов ───
    // Максимум = min(баланс, 30% от суммы товаров со скидкой, без доставки)
    const subtotalAfterDiscount = Math.max(0, (totals.subtotalRub || 0) - (totals.discountRub || 0));
    const maxBonusSpend = isAuthenticated
        ? Math.min(bonusBalance || 0, Math.floor(subtotalAfterDiscount * 0.30))
        : 0;
    const bonusUsed = applyBonus ? maxBonusSpend : 0;
    const totalAfterBonus = Math.max(0, (totals.totalRub || 0) - bonusUsed);

    // ─── НОВОЕ: загрузка профиля и сохранённых адресов при открытии чекаута ───
    useEffect(() => {
        if (!isOpen || !isAuthenticated || profileLoaded) return;
        let stop = false;
        (async () => {
            try {
                const [rp, ra] = await Promise.all([
                    fetch('/api/account/profile', { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
                    fetch('/api/account/addresses', { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
                ]);
                if (stop) return;
                if (rp) {
                    setForm((prev) => ({
                        full_name: prev.full_name || rp.full_name || '',
                        phone: prev.phone || rp.phone || '',
                        email: prev.email || rp.email || '',
                    }));
                }
                if (ra && ra.addresses) {
                    setSavedAddresses(ra.addresses);
                    // если есть основной адрес — сразу подставляем
                    const def = ra.addresses.find((a) => a.is_default) || ra.addresses[0];
                    if (def) {
                        setPickup({
                            id: 'saved_' + def.id,
                            name: def.network === 'ozon' ? 'Ozon' : (def.network === 'wb' ? 'Wildberries' : 'Адрес'),
                            address: def.address,
                        });
                    }
                }
                setProfileLoaded(true);
            } catch (_) { }
        })();
        return () => { stop = true; };
    }, [isOpen, isAuthenticated, profileLoaded]);

    if (!isOpen) return null;

    const setField = (field) => (e) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const deliveryAddress = (pickup && pickup.address) || '';

    const validate = () => {
        if (!form.full_name.trim()) return 'Укажите ФИО';
        if (!form.phone.trim()) return 'Укажите телефон';
        if (!form.email.includes('@')) return 'Укажите корректный email — на него придёт чек';
        if (!deliveryAddress) {
            return 'Введите адрес пункта выдачи в блоке доставки — он подтвердится автоматически';
        }
        if (!consent) {
            return 'Для оформления заказа необходимо согласие на обработку персональных данных';
        }
        return '';
    };

    const handlePay = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            setErrorKind('validation');
            goal('checkout_validation_error');
            clientLog('warn', {
                event: 'checkout_validation_error',
                reason: validationError,
                filled: {
                    full_name: !!form.full_name.trim(),
                    phone: !!form.phone.trim(),
                    email: !!form.email.trim(),
                },
                pickup_selected: !!pickup,
            });
            return;
        }
        setError('');
        setProcessing(true);
        goal('checkout_submit');
        clientLog('info', {
            event: 'checkout_submit',
            items: cart.map((i) => `${i.id}x${i.qty}`),
            totals: {
                subtotal: totals.subtotalRub,
                discount: totals.discountRub,
                delivery: totals.deliveryRub,
                total: totals.totalRub,
                zone: zoneId,
            },
            delivery: deliveryAddress,
            metrika_id: metrikaId || null,
        });
        // Если введён новый адрес и клиент авторизован — сохраняем в список
        if (isAuthenticated && pickup && !String(pickup.id || '').startsWith('saved_') && deliveryAddress) {
            try {
                await fetch('/api/account/addresses', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        label: '',
                        address: deliveryAddress,
                        network: pickup.name?.toLowerCase().includes('ozon') ? 'ozon' : (pickup.name?.toLowerCase().includes('wb') ? 'wb' : ''),
                        is_default: false,
                    }),
                });
            } catch (_) { }
        }
        try {
            const data = await withTimeout(
                createOrder({
                    user_id: userId,
                    metrika_client_id: metrikaId || null,   
                    items: cart.map((item) => ({ id: item.id, quantity: item.qty })),
                    customer: { ...form, address: deliveryAddress },
                    delivery_zone: zoneId,
                    consent: true,
                    consent_policy_version: '2026-09-18',
                    marketing_consent: marketing,
                    use_bonus_amount: bonusUsed,
                })
            );
            const payUrl = data && (data.payment_url || data.PaymentURL || data.confirmation_url);
            if (!payUrl) throw new Error('Сервер не вернул ссылку на оплату');

            // НОВОЕ: сохраняем последний заказ — его прочитает /payment-success
            try {
                localStorage.setItem('last_order', JSON.stringify({
                    order_id: data.order_id || null,
                    amount_rub: Number(totalAfterBonus || 0),
                    bonus_used: bonusUsed,
                    items: cart.map((i) => ({
                        id: i.id, name: i.name,
                        price: Number(i.price || 0), qty: i.qty,
                    })),
                    created_at: Date.now(),
                }));
            } catch (_) { }

            goal('payment_redirect');
            if (bonusUsed > 0 && onBonusUsed) {
                onBonusUsed(bonusUsed);
            }
            clientLog('info', { event: 'payment_redirect', pay_url: payUrl });
            window.location.href = payUrl;
        } catch (e) {
            const msg = errText(e);
            setError(
                `Не удалось создать заказ: ${msg}. Попробуйте ещё раз — или позвоните нам, мы оформим заказ вручную.`
            );
            setErrorKind('fatal');
            goal('checkout_error');
            clientLog('error', {
                event: 'checkout_error',
                message: msg,
                stack: e && e.stack ? String(e.stack).slice(0, 800) : null,
            });
            setProcessing(false);
        }
    };

    const inputCls =
        'w-full p-3 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A] transition';

    const QtyButton = ({ onClick, children, disabled }) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`w-8 h-8 rounded-full border flex items-center justify-center text-lg leading-none transition ${disabled
                ? 'border-[#E8E3DD] text-gray-300 cursor-not-allowed'
                : 'border-[#E8E3DD] text-[#1C1917] hover:border-[#6B7A6A] hover:text-[#6B7A6A] active:bg-[#F5F0EB]'
                }`}
        >
            {children}
        </button>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-50 flex justify-end">
            <div className="w-full max-w-md bg-white h-full shadow-xl p-6 overflow-y-auto">
                <div className="flex justify-between items-center border-b pb-4">
                    <h3 className="serif text-2xl">{step === 'cart' ? 'Корзина' : 'Оформление заказа'}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Закрыть">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {step === 'cart' && (
                    <>
                        <div className="py-4 space-y-3">
                            {cart.length === 0 ? (
                                <p className="text-gray-400 text-center py-6">Корзина пуста</p>
                            ) : (
                                cart.map((item) => {
                                    const photo = item.photo || 'https://placehold.co/96x96/F5F0EB/6B7A6A?text=PS';
                                    return (
                                        <div key={item.id} className="flex items-start gap-3 border-b pb-3">
                                            <img src={photo} alt="" className="w-16 h-16 rounded-xl object-cover bg-[#F5F0EB] shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="font-medium text-sm leading-tight">
                                                        {item.name} {item.volume ? <span className="text-gray-500 font-normal">({item.volume})</span> : ''}
                                                    </p>
                                                    <button
                                                        onClick={() => removeFromCart(item.id)}
                                                        className="shrink-0 text-red-400 hover:text-red-600 transition text-sm"
                                                        aria-label="Удалить товар"
                                                        title="Удалить товар"
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-0.5">{fmtRub(item.price)} ₽</p>
                                                {item.price_info?.discount_amount > 0 && (
                                                    <p className="text-[10px] text-[#6B7A6A] font-medium">
                                                        Скидка {Math.round(item.price_info.discount_amount)} ₽
                                                    </p>
                                                )}
                                                <div className="flex items-center gap-2 mt-2">
                                                    <QtyButton onClick={() => updateQuantity(item.id, item.qty - 1)} aria-label="Уменьшить количество">−</QtyButton>
                                                    <span className="min-w-[28px] text-center font-medium text-sm">{item.qty}</span>
                                                    <QtyButton onClick={() => updateQuantity(item.id, item.qty + 1)} aria-label="Увеличить количество">+</QtyButton>
                                                    <span className="ml-auto text-sm font-semibold text-[#1C1917]">
                                                        {fmtRub(item.price * item.qty)} ₽
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {recommendations.length > 0 && (
                            <div className="pt-4 mt-2 border-t">
                                <p className="text-sm font-medium mb-2">Дополнит ваш уход:</p>
                                {recommendations.map((p) => (
                                    <div key={p.id} className="flex items-center justify-between gap-2 py-2 border-b border-dashed border-[#E8E3DD] last:border-0">
                                        <div className="min-w-0">
                                            <p className="text-xs font-medium truncate">{p.name}</p>
                                            <p className="text-xs text-gray-400">{Math.round(p.price)} ₽</p>
                                        </div>
                                        <button
                                            onClick={() => addToCart(p)}
                                            className="shrink-0 text-xs font-semibold text-[#6B7A6A] border border-[#6B7A6A] rounded-full px-3 py-1 hover:bg-[#6B7A6A] hover:text-white transition"
                                        >
                                            + Добавить
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="border-t pt-4 mt-4">
                            {/* ─── НОВОЕ: применение бонусов ─── */}
                            {isAuthenticated && maxBonusSpend > 0 && (
                                <label className="flex items-start gap-2 mb-3 p-3 rounded-xl bg-[#F5F0EB] cursor-pointer hover:bg-[#EFE9E2] transition">
                                    <input
                                        type="checkbox"
                                        checked={applyBonus}
                                        onChange={(e) => setApplyBonus(e.target.checked)}
                                        className="mt-0.5"
                                    />
                                    <span className="text-sm flex-1">
                                        <strong className="text-[#1C1917]">💎 Списать {maxBonusSpend} баллов</strong>
                                        <span className="block text-xs text-gray-500 mt-0.5">
                                            Баланс: {bonusBalance} баллов. Доступно к списанию: {maxBonusSpend} ₽ (30% от суммы товаров).
                                        </span>
                                    </span>
                                </label>
                            )}
                            {isAuthenticated && bonusBalance > 0 && maxBonusSpend === 0 && (
                                <p className="text-xs text-gray-400 mb-3">
                                    💎 У вас {bonusBalance} баллов. Списание станет доступно от заказа 100 ₽.
                                </p>
                            )}

                            <div className="flex justify-between font-bold text-lg">
                                <span>Итого</span>
                                <span>{fmtRub(totals.subtotalRub)} ₽</span>
                            </div>
                            {(() => {
                                if (!cartPromos.length || totals.subtotalRub <= 0) return null;
                                const distinct = cart.length;
                                let bestProgress = null;
                                cartPromos.forEach((p) => {
                                    if (p.type === 'cart_total') {
                                        const diff = p.min_amount - totals.subtotalRub;
                                        if (diff > 0 && (!bestProgress || diff < bestProgress.diff)) {
                                            bestProgress = { diff, type: 'amount', promo: p };
                                        }
                                    }
                                    if (p.type === 'cart_quantity') {
                                        const diff = p.min_quantity - distinct;
                                        if (diff > 0 && (!bestProgress || diff < bestProgress.diff)) {
                                            bestProgress = { diff, type: 'items', promo: p };
                                        }
                                    }
                                });
                                if (totals.appliedPromo) {
                                    return (
                                        <p className="text-xs text-[#6B7A6A] font-medium mt-1">
                                            🎉 Скидка {totals.appliedPromo.discount_value}% применена
                                        </p>
                                    );
                                }
                                if (bestProgress) {
                                    return bestProgress.type === 'amount' ? (
                                        <p className="text-xs text-gray-500 mt-1">
                                            До скидки {bestProgress.promo.discount_value}% не хватает {fmtRub(bestProgress.diff)} ₽
                                        </p>
                                    ) : (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Добавьте ещё {bestProgress.diff} {bestProgress.diff === 1 ? 'товар' : 'товара'} для скидки {bestProgress.promo.discount_value}%
                                        </p>
                                    );
                                }
                                return null;
                            })()}
                            <button
                                onClick={() => { goal('start_checkout'); setStep('checkout'); }}
                                disabled={cart.length === 0}
                                className={`w-full mt-4 py-3 rounded-lg font-medium transition ${cart.length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'btn-primary'}`}
                            >
                                Оформить заказ
                            </button>
                        </div>
                    </>
                )}

                {step === 'checkout' && (
                    <div className="py-4 space-y-3">
                        <input className={inputCls} placeholder="ФИО получателя" value={form.full_name} onChange={setField('full_name')} />
                        <input className={inputCls} placeholder="Телефон (+7...)" value={form.phone} onChange={setField('phone')} />
                        <input className={inputCls} placeholder="Email (для чека)" value={form.email} onChange={setField('email')} />

                        <p className="text-sm font-medium pt-1">Регион доставки:</p>
                        <div className="space-y-2">
                            {DELIVERY_ZONES.map((z) => (
                                <button
                                    key={z.id}
                                    type="button"
                                    onClick={() => setZoneId(z.id)}
                                    className={`w-full text-left p-3 rounded-lg border text-sm transition ${zoneId === z.id ? 'border-[#6B7A6A] bg-[#F5F0EB]' : 'border-[#E8E3DD] bg-white'}`}
                                >
                                    <span className="font-medium">{z.label}</span>
                                    <span className="block text-xs text-gray-500 mt-0.5">
                                        Бесплатно от {fmtRub(z.freeFrom)} ₽ · иначе {z.fee} ₽ · {z.eta}
                                    </span>
                                </button>
                            ))}
                        </div>

                        <p className="text-sm font-medium pt-1">Доставка — пункт выдачи:</p>

                        {savedAddresses.length > 0 && (
                            <div className="space-y-1 mb-2">
                                <p className="text-xs text-gray-500">Ваши сохранённые адреса:</p>
                                {savedAddresses.map((a) => {
                                    const selected = pickup && pickup.id === 'saved_' + a.id;
                                    return (
                                        <button
                                            key={a.id}
                                            type="button"
                                            onClick={() => setPickup({
                                                id: 'saved_' + a.id,
                                                name: a.network === 'ozon' ? 'Ozon' : (a.network === 'wb' ? 'Wildberries' : 'Адрес'),
                                                address: a.address,
                                            })}
                                            className={`w-full text-left p-3 rounded-lg border text-sm transition ${selected ? 'border-[#6B7A6A] bg-[#F5F0EB]' : 'border-[#E8E3DD] bg-white hover:border-[#6B7A6A]'}`}
                                        >
                                            <span className="font-medium">{a.label || 'Адрес'}</span>
                                            {a.is_default && <span className="ml-2 text-[10px] text-[#6B7A6A]">основной</span>}
                                            <span className="block text-xs text-gray-500 mt-0.5">{a.address}</span>
                                        </button>
                                    );
                                })}
                                <button
                                    type="button"
                                    onClick={() => setPickup(null)}
                                    className={`w-full text-left p-3 rounded-lg border text-sm transition ${!pickup ? 'border-[#6B7A6A] bg-[#F5F0EB]' : 'border-[#E8E3DD] bg-white hover:border-[#6B7A6A]'}`}
                                >
                                    ✏️ Ввести новый адрес
                                </button>
                            </div>
                        )}

                        {(!pickup || !String(pickup.id || '').startsWith('saved_')) && (
                            <PickupSelector onSelect={setPickup} selected={pickup} />
                        )}

                        {error && (
                            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                                ⚠️ {error}
                                {errorKind === 'fatal' && (
                                    <>
                                        <br />
                                        <span className="text-xs">
                                            Не получилось оформить заказ онлайн? Позвоните нам:{' '}
                                            <a href="tel:+79166546541" className="underline font-semibold">+7 916 654-65-41</a>{' '}
                                            — оформим вручную.
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                        <label className="flex items-start gap-2 text-xs text-gray-600">
                            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
                            <span>
                                Я соглашаюсь с{' '}
                                <Link to="/privacy" target="_blank" rel="noopener" className="underline text-[#6B7A6A]">политикой обработки персональных данных</Link>{' '}
                                и принимаю условия{' '}
                                <Link to="/offer" target="_blank" rel="noopener" className="underline text-[#6B7A6A]">публичной оферты</Link>
                            </span>
                        </label>
                        <label className="flex items-start gap-2 text-xs text-gray-500">
                            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5" />
                            <span>Хочу получать письма об акциях и новинках (необязательно)</span>
                        </label>
                        <div className="border-t pt-3 space-y-1 text-sm">
                            <div className="flex justify-between"><span>Товары</span><span>{fmtRub(totals.subtotalRub)} ₽</span></div>
                            {totals.discountRub > 0 && (
                                <div className="flex justify-between text-[#6B7A6A] font-medium">
                                    <span>
                                        Скидка {totals.appliedPromo?.discount_value}
                                        {totals.appliedPromo?.discount_type === 'percent' ? '%' : ' ₽'}
                                        {totals.appliedPromo?.name ? ` · ${totals.appliedPromo.name}` : ''}
                                    </span>
                                    <span>−{fmtRub(totals.discountRub)} ₽</span>
                                </div>
                            )}
                            {bonusUsed > 0 && (
                                <div className="flex justify-between text-[#6B7A6A] font-medium">
                                    <span>💎 Баллы</span>
                                    <span>−{fmtRub(bonusUsed)} ₽</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span>Доставка · {totals.zone.eta}</span>
                                <span>{totals.deliveryRub === 0 ? 'Бесплатно' : `${fmtRub(totals.deliveryRub)} ₽`}</span>
                            </div>
                            <Link to="/delivery" target="_blank" rel="noopener noreferrer" className="block text-xs text-gray-500 underline">
                                Условия доставки и оплаты
                            </Link>
                        </div>

                        <div className="flex justify-between font-bold text-lg pt-2 border-t">
                            <span>К оплате</span><span>{fmtRub(totalAfterBonus)} ₽</span>
                        </div>
                        <button onClick={handlePay} disabled={processing} className="btn-primary w-full py-3 rounded-lg">
                            {processing ? 'Создание платежа…' : 'Оплатить'}
                        </button>
                        <button onClick={() => setStep('cart')} className="btn-outline w-full py-2 rounded-lg text-sm">
                            ← Назад к корзине
                        </button>
                        <p className="text-xs text-gray-400 text-center">Безопасная оплата через Т-Банк · чек по 54-ФЗ</p>
                        <p className="text-xs text-gray-400 text-center">
                            Вопросы по заказу: <a href="mailto:sale@puresynergy.ru" className="underline">sale@puresynergy.ru</a>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartModal;